import { has, isObject } from 'lodash';

export type Paragraph<
  Name extends string = string,
  Content extends object = {},
> = {
  __typename: Name;
} & Content;

type EmptyParagraph = Paragraph<'ParagraphEmpty'>;

function isParagraphOfType<Type extends string>(
  paragraph: Paragraph<any>,
  type: Type,
): paragraph is Paragraph<Type> {
  return paragraph.__typename === type;
}

function isEmptyParagraph(input: Paragraph): boolean {
  return (
    isParagraphOfType(input, 'ParagraphEmpty') ||
    (isParagraphOfType(input, 'ParagraphNewsTeaser') &&
      // @ts-ignore
      !input.newsContent)
  );
}

function isParagraph<T extends Paragraph>(
  input: T | undefined,
): input is Exclude<T, undefined | EmptyParagraph> {
  return (
    isObject(input) && has(input, '__typename') && !isEmptyParagraph(input)
  );
}

export function removeEmptyParagraphs<T extends Paragraph>(
  input?: Array<T | undefined>,
): Array<Exclude<T, undefined | EmptyParagraph>> {
  return input ? input?.filter(isParagraph) : [];
}

export type ParagraphGroup<T extends Paragraph> = {
  __typename: `${T['__typename']}__Group`;
  items?: Array<T>;
};

export type WithGroupedParagraphs<
  AllParagraphs extends Array<Paragraph>,
  GroupedParagraph extends AllParagraphs[number]['__typename'],
> = Array<
  | AllParagraphs[number]
  | ParagraphGroup<Extract<AllParagraphs[number], Paragraph<GroupedParagraph>>>
>;

export function groupParagraphSequences<
  AllParagraphs extends Array<Paragraph>,
  GroupedParagraph extends AllParagraphs[number]['__typename'],
>(
  input: AllParagraphs,
  type: GroupedParagraph,
  threshold: number = 2,
): Array<AllParagraphs[number] | ParagraphGroup<Paragraph<GroupedParagraph>>> {
  const output: Array<
    AllParagraphs[number] | ParagraphGroup<Paragraph<GroupedParagraph>>
  > = [];
  let currentGroup: Array<Paragraph<GroupedParagraph>> = [];

  function exhaustGroup() {
    if (currentGroup.length > 0) {
      if (currentGroup.length >= threshold) {
        output.push({
          __typename: `${type}__Group`,
          items: currentGroup,
        });
      } else {
        currentGroup.forEach((groupItem) => {
          output.push(groupItem);
        });
      }
      currentGroup = [];
    }
  }

  input.forEach((item) => {
    if (isParagraphOfType(item, type)) {
      currentGroup.push(item);
    } else {
      exhaustGroup();
      output.push(item);
    }
  });
  exhaustGroup();
  return output;
}
